import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useEnvironmentContext} from 'src/hooks/useEnvironmentContext';

import {getCountryCallingCode} from 'libphonenumber-js';
import useAgencyConfig from 'src/hooks/useAgencyConfig';
import useI18n from 'src/hooks/useI18n';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {validatePhone} from 'src/utils/phone';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';
import {
  CONSTANTS,
  getTranslatedText,
  ALLOWED_COUNTRY_CODES,
} from 'src/components/authentication/contants';
import {AuthFlow} from 'src/components/authentication/auth-flow';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {
  TitleMedium,
  SubTitleMedium,
  BodySmall,
  BodyLarge,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {Dropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import type {
  AuthSideBarComp,
  PhoneRegistrationFormProps,
} from 'src/types/authentication';

import {
  registerNewPhone,
  performSignIn,
} from 'src/action-creators/authentication';

import {
  selectUserLoginId,
  selectUserEmail,
  selectNewPhoneRegistrationError,
  selectCurrentAgency,
  selectMfaAuthChannel,
} from 'src/selectors/authentication';
import classify from 'src/utils/classify';
import css from './phone-registration.css';
import maincss from 'src/components/authentication/main.css';


type PhoneRegistrationProps = AuthSideBarComp & {
  hideSkip: boolean;
};

const PhoneRegistration = (props: PhoneRegistrationProps) => {
  const {goToPage, hideSkip} = props;
  const countryCallPrefix: Array<MenuOption> = [];
  const regionNames = new Intl.DisplayNames(['en'], {
    type: 'region',
    // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'locale' does not exist in type 'DisplayNamesOptions'.
    locale: 'en-GB',
  });
  ALLOWED_COUNTRY_CODES.forEach((countryCode) => {
    const dialCodeFromLibrary = getCountryCallingCode(countryCode);
    const dialCode = `+${dialCodeFromLibrary}`;
    const countryName = regionNames.of(countryCode);
    // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'MenuOption'.
    if (!countryCallPrefix.includes(dialCode)) {
      countryCallPrefix.push({
        label: `(${dialCode}) ${countryName}`,
        key: dialCode,
      });
    }
  });

  const defaultPhoneCountry = useAgencyConfig('defaultPhoneCountry');

  let defaultCode;
  if (defaultPhoneCountry) {
    defaultCode = {
      // @ts-expect-error - TS2345 - Argument of type 'string | number | true' is not assignable to parameter of type 'CountryCode'.
      label: `+${getCountryCallingCode(defaultPhoneCountry)}`,
      // @ts-expect-error - TS2345 - Argument of type 'string | number | true' is not assignable to parameter of type 'CountryCode'.
      key: `+${getCountryCallingCode(defaultPhoneCountry)}`,
    };
  } else {
    defaultCode = {
      label: '+1',
      key: '+1',
    };
  }

  return (
    <PhoneRegistrationForm
      goToPage={goToPage}
      countryCodes={countryCallPrefix}
      defaultCode={defaultCode}
      hideSkip={hideSkip}
    />
  );
};

const PhoneRegistrationForm = (props: PhoneRegistrationFormProps) => {
  const {makeKey, t} = useLabelTranslation();
  const {goToPage, countryCodes, defaultCode, hideSkip} = props;
  const dispatch = useDispatch();
  const userEmail = useSelector(selectUserEmail);
  const loginId = useSelector(selectUserLoginId);
  const apiError = useSelector(selectNewPhoneRegistrationError);
  const agency = useSelector(selectCurrentAgency);
  const authChannel = useSelector(selectMfaAuthChannel);
  const isTotpChannel = authChannel === 'TOTP';

  const [countryCode, setCountryCode] = React.useState<MenuOption>(defaultCode);
  const [userPhone, setUserPhone] = React.useState<string>('');
  const [phoneError, setPhoneError] = React.useState<string>('');
  const [isSendingOtp, setIsSendingOtp] = React.useState<boolean>(false);

  const sentOtpForPhone = async () => {
    const CountryPrefix = countryCode.key;
    const prefix = CountryPrefix ? CountryPrefix : '';
    const enteredPhone = `${prefix}${userPhone}`;

    const countryPrefixLength = CountryPrefix ? CountryPrefix.length : 0;
    if (
      enteredPhone.length !== 10 + countryPrefixLength ||
      !validatePhone(enteredPhone)
    ) {
      setPhoneError(ERROR_CODES['InvalidPhoneEntered']);
      return;
    } else {
      setPhoneError('');
    }

    setIsSendingOtp(true);
    const apiResponse = await dispatch(
      registerNewPhone(enteredPhone, userEmail, loginId),
    );
    setIsSendingOtp(false);
    // @ts-expect-error - TS2774 - This condition will always return true since this function is always defined. Did you mean to call it instead?
    if (apiResponse) {
      goToPage(AuthFlow.SMS_REGISTRATION.OTP_INPUT);
    }
  };

  const handleCountryCodeChange = (value: MenuOption) => {
    setCountryCode(value);
  };

  const handlePhoneChange = (value: string) => {
    if (phoneError) {
      setPhoneError('');
    }
    setUserPhone(value);
  };

  const handleSkip = async () => {
    if (isTotpChannel) {
      goToPage(AuthFlow.TOTP.SIGN_IN, 'TOTP');
    } else {
      await dispatch(performSignIn(agency));
    }
  };

  const {isExtension} = useEnvironmentContext();

  return (
    <div className={css.phoneRegistrationPage}>
      <div className={css.titleSection}>
        {isExtension ? (
          <SubTitleMedium className={maincss.alignSelfCenter}>
            {isTotpChannel
              ? t('Register_phone_number', 'Register phone number')
              : getTranslatedText(t, CONSTANTS.PHONE_REGISTARTION_TITLE)}
          </SubTitleMedium>
        ) : (
          <TitleMedium>
            {isTotpChannel
              ? t('Register_phone_number', 'Register phone number')
              : getTranslatedText(t, CONSTANTS.PHONE_REGISTARTION_TITLE)}
          </TitleMedium>
        )}
        {isExtension ? (
          <BodySmall className={maincss.textAlignCenter}>
            {getTranslatedText(t, CONSTANTS.PHONE_REGISTARTION_SUBTEXT)}
          </BodySmall>
        ) : (
          <BodyLarge className={maincss.subText}>
            {getTranslatedText(t, CONSTANTS.PHONE_REGISTARTION_SUBTEXT)}
          </BodyLarge>
        )}
      </div>
      <div className={css.inputSection}>
        <FormLabelSmall color="secondary">
          {t('Phone_number', 'Phone number')}
        </FormLabelSmall>
        <div>
          <div>
            <Dropdown
              // @ts-expect-error - TS2345 - Argument of type 'ChangeEvent<HTMLInputElement> | MenuOption' is not assignable to parameter of type 'MenuOption'.
              onChange={(e) => handleCountryCodeChange(e)}
              dropdownInputText={countryCode.key}
              menu={{
                options: countryCodes,
                selectedKeys: [countryCode.key],
                classNames: {wrapper: css.menuWrapper},
              }}
              size="medium"
              classNames={{wrapper: css.countryDropDown}}
              disabled
            />
            <div className={css.phoneInput}>
              <Input
                size="medium"
                type="tel"
                value={userPhone}
                onChange={(e) => {
                  handlePhoneChange(e.target.value);
                }}
                error={!!phoneError}
                errorText={t(makeKey(phoneError), phoneError)}
              />
            </div>
          </div>
        </div>
        <Button
          size="medium"
          type="primary"
          classNames={{wrapper: css.nextBtn}}
          onClick={sentOtpForPhone}
          disabled={isSendingOtp}
        >
          {isSendingOtp ? t('Sending_Otp', 'Sending Otp') : t('Next', 'Next')}
        </Button>

        {!!apiError && (
          <InContextAlert
            semantic="danger"
            classNames={{wrapper: maincss.apiError}}
          >
            {t(
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
              makeKey(ERROR_CODES[apiError] || apiError),
              // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
              ERROR_CODES[apiError] || apiError,
            )}
          </InContextAlert>
        )}

        {!hideSkip && (
          <div
            className={classify(css.infoLink, {[css.infoLinkExt]: isExtension})}
          >
            <Link
              onClick={handleSkip}
              as={isExtension ? 'buttonTextExtraSmall' : 'buttonTextMedium'}
            >
              {t('Skip_for_now', 'Skip for now')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhoneRegistration;
